import React, { useState } from 'react';
import { Alert, Box, Button, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { EmptyState, ErrorState, PageTitle, StatusChip, useAsync } from '../PortalUi';
import { fetchFundRequests, portalErrorMessage, submitFundRequest } from '../../../../services/request/CustomerPortal';
import { formatDate, fundRequestError, inr, monthlyRate, PortalValidationKey, wholeRupees } from '../../../../utils/customerPortalFormat';
import { useTranslation } from '../../../../i18n';

export default function PortalRequestFunds() {
  const { t } = useTranslation();
  const requests = useAsync(fetchFundRequests, []);
  const [requested, setRequested] = useState('');
  const [minimum, setMinimum] = useState('');
  const [rate, setRate] = useState('');
  const [purpose, setPurpose] = useState('');
  const [problem, setProblem] = useState<PortalValidationKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const input = {
      requestedAmount: wholeRupees(requested) ?? 0,
      minimumAmount: wholeRupees(minimum) ?? 0,
      monthlyInterestRate: monthlyRate(rate) ?? 0,
      purpose,
    };
    const invalid = fundRequestError(input);
    setProblem(invalid ?? null);
    if (invalid) {
      setError(null);
      return;
    }
    setSending(true);
    setError(null);
    try {
      await submitFundRequest(input);
      setSent(true);
      setRequested('');
      setMinimum('');
      setRate('');
      setPurpose('');
      requests.reload();
    } catch (err) {
      setError(portalErrorMessage(err, t('portal.requestFunds.sendError')));
    } finally {
      setSending(false);
    }
  };

  const message = problem ? t(`portal.validation.${problem}`) : error;

  return (
    <Box>
      <PageTitle
        kicker={t('portal.requestFunds.kicker')}
        title={t('portal.requestFunds.title')}
        subtitle={t('portal.requestFunds.subtitle')}
      />
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
        <Paper component="form" onSubmit={submit} sx={{ p: 3, display: 'grid', gap: 2 }}>
          {sent ? (
            <Alert severity="success" onClose={() => setSent(false)}>
              {t('portal.requestFunds.sent')}
            </Alert>
          ) : null}
          {message ? <Alert severity="error">{message}</Alert> : null}
          <TextField
            label={t('portal.requestFunds.exact')}
            value={requested}
            onChange={event => setRequested(event.target.value)}
            inputMode="numeric"
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />
          <TextField
            label={t('portal.requestFunds.minimum')}
            value={minimum}
            onChange={event => setMinimum(event.target.value)}
            inputMode="numeric"
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />
          <TextField
            label={t('portal.requestFunds.rate')}
            value={rate}
            onChange={event => setRate(event.target.value)}
            inputMode="decimal"
            helperText={t('portal.requestFunds.rateHelper')}
            InputProps={{ endAdornment: <InputAdornment position="end">{t('portal.requestFunds.rateSuffix')}</InputAdornment> }}
          />
          <TextField
            label={t('portal.requestFunds.purpose')}
            value={purpose}
            onChange={event => setPurpose(event.target.value)}
            multiline
            minRows={3}
            inputProps={{ maxLength: 500 }}
          />
          <Button type="submit" variant="contained" size="large" disabled={sending}>
            {sending ? t('common.sending') : t('portal.requestFunds.submit')}
          </Button>
        </Paper>

        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('portal.requestFunds.listTitle')}
          </Typography>
          {requests.error ? <ErrorState message={requests.error} onRetry={requests.reload} /> : null}
          {requests.data && requests.data.length === 0 ? <EmptyState title={t('portal.requestFunds.empty')} /> : null}
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {(requests.data ?? []).map(request => (
              <Paper key={request.id} sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>{inr(request.requestedAmount)}</Typography>
                  <StatusChip status={request.status} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('portal.requestFunds.summaryLine', {
                    amount: inr(request.minimumAmount),
                    rate: request.monthlyInterestRate ?? '—',
                    date: formatDate(request.createdDate) ?? '',
                  })}
                </Typography>
                {request.purpose ? <Typography sx={{ mt: 1 }}>{request.purpose}</Typography> : null}
                {request.adminNote ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('portal.common.noteFromWhatzneu', { note: request.adminNote })}
                  </Typography>
                ) : null}
              </Paper>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
